<script setup>
// Vue.
import { computed, ref, onMounted } from 'vue';
// Components.
import EmblaCarousel from 'embla-carousel';
import ResponsiveImage from './ResponsiveImage';
// Utils.
import isMobile from '~/js/utils/is-mobile';

// Props.
const props = defineProps({
  numbers: {
    type: Array,
    required: true,
    default: () => [],
  },
});

// Data.
const activeItemId = ref(0);
const onMobile = ref(isMobile());
const emblaElement = ref(null);
const emblaCarousel = ref(null);
const canScrollPrev = ref(false);
const canScrollNext = ref(true);

// Computed.
const images = computed(() => {
  const currentItem = props.numbers[activeItemId.value] || {};
  return currentItem?.image;
});

// Mounted.
onMounted(() => {
  // Update checker for mobile.
  window.addEventListener('resize', () => {
    onMobile.value = isMobile();
  });

  // Init embla on mobile.
  // Using vanilla embla because Vue embla has issues with being nested.
  if (onMobile.value) {
    emblaCarousel.value = EmblaCarousel(emblaElement.value, {
      draggable: false,
      watchDrag: false,
    });
    emblaCarousel.value.on('select', () => {
      canScrollPrev.value = emblaCarousel.value.canScrollPrev();
      canScrollNext.value = emblaCarousel.value.canScrollNext();
      activeItemId.value = emblaCarousel.value.selectedScrollSnap();
    });
  }
});
</script>

<template>
  <div class="target-numbers-tab">
    <div ref="emblaElement" class="target-numbers-tab__numbers embla">
      <div class="embla__container target-numbers-tab__container">
        <!-- Nested slides. -->
        <div
          v-for="(item, index) in numbers"
          :key="item.id"
          class="embla__slide"
          :class="{ 'embla__slide--active': activeItemId === item.id }">
          <!-- Text. -->
          <div class="target-numbers-tab__number" @click="activeItemId = index">
            <span class="target-numbers-tab__number-figure">
              <strong>{{ item.figure }}</strong>
            </span>
            <p class="target-numbers-tab__number-fact">
              {{ item.fact }}
            </p>
          </div>
          <!-- Image and controls (on mobile only). -->
          <div v-if="onMobile && images" class="target-numbers-tab__image">
            <ResponsiveImage :images="item.image" />
          </div>
        </div>
      </div>
      <!-- Controls (on mobile only). -->
      <div
        v-if="onMobile && numbers.length > 1 && images"
        class="target-numbers-tab__controls">
        <button
          class="embla__button embla__button--prev"
          @click="emblaCarousel.scrollPrev()"
          :disabled="!canScrollPrev">
          <i class="fa-regular fa-angle-left"></i>
        </button>
        <button
          class="embla__button embla__button--next"
          @click="emblaCarousel.scrollNext()"
          :disabled="!canScrollNext">
          <i class="fa-regular fa-angle-right"></i>
        </button>
      </div>
    </div>
    <!-- Shared image container on desktop. -->
    <div v-if="!onMobile && images" class="target-numbers-tab__image">
      <responsive-image :images="images" />
    </div>
  </div>
</template>
